import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { TemplateObjectType } from 'webapp/enums/enums';

import type { BoardLoop, Report, Template } from 'webapp/models-custom';
import Board from 'webapp/models-custom/board';
import BoardRecord from 'webapp/models/board';
import type BoardLoopRecord from 'webapp/models/board-loop';

export default class BoardsAccountTemplatesController extends Controller {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('router') declare routerService: Service.Router;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	override get model(): (Board | BoardLoop)[] {
		return [
			...this.mmBoardsService.accountTemplateBoardModels,
			...this.mmBoardLoopsService.accountTemplateBoardLoopModels,
		];
	}

	get currentSpaceModels(): (Board | BoardLoop)[] {
		return this.model.filter((model) => model.isForCurrentSpace);
	}

	get otherSpaceModels(): (Board | BoardLoop)[] {
		return this.model.filter((model) => !model.isForCurrentSpace);
	}

	get hasCurrentSpaceModels(): boolean {
		return !!this.currentSpaceModels.length;
	}

	get showRecommendedBoards(): boolean {
		return !this.accountService.isClient && !!this.mmBoardsService.normalBoardModels.length;
	}

	get showRecommendedAccountTemplates(): boolean {
		return !!this.otherSpaceModels.length;
	}

	@action
	createTemplate(): void {
		this.mmBoardsService.checkToOpenAccountTemplateEditPopup();
	}

	@action
	useTemplate(model: Template): void {
		const afterUse = (record: BoardRecord | BoardLoopRecord) => {
			const isAccountTemplatesPath = this.routerService.currentRouteName?.startsWith(
				'boards.account-templates',
			);

			const routeName =
				model.objectType === TemplateObjectType.BOARD ? 'boards.index' : 'boards.loops';

			let afterTransition = () => {
				/* do nothing */
			};

			if (isAccountTemplatesPath) {
				afterTransition = () => {
					if (record instanceof BoardRecord) {
						const boardModel = this.mmBoardsService.createModel(record);
						this.mmBoardsService.openPreview(boardModel, null);
					} else {
						const boardLoopModel = this.mmBoardLoopsService.createModel(record);
						this.mmBoardLoopsService.openPreviewPopup(boardLoopModel);
					}
				};
			}

			this.navigationService.transitionToRoute(routeName).then(afterTransition);
		};

		if (model.objectType === TemplateObjectType.BOARD) {
			this.mmBoardsService.createBoardFromTemplate(model, 'account_template', afterUse);
		} else {
			this.mmBoardLoopsService.createBoardLoopFromTemplate(
				model,
				'account_template_loop',
				afterUse,
			);
		}
	}

	@action
	previewTemplate(model: Template): void {
		this.mmTemplatesService.openPreviewPopup(model, () => {
			this.useTemplate(model);
		});
	}

	@action
	openTemplatePermissions(model: Board | Report): void {
		this.mmBoardsService.openBoardAccessTypePopup(model as Board);
	}

	@action
	editTemplate(model: Board | Report | BoardLoop): void {
		if (model instanceof Board) {
			this.mmBoardsService.editBoard(model);
		} else {
			this.mmBoardLoopsService.openInDesigner(model as BoardLoop);
		}
	}

	@action
	deleteTemplate(model: Board | Report | BoardLoop): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const template = (model as Board | BoardLoop).templateModel;
		if (template) {
			void this.mmTemplatesService.deleteTemplate(template);
		}
	}
}
